import CustomerContactDisplay from "@/modules/common/components/customer-contact";
import { resolveCustomerContact } from "@/modules/common/utils/customer-contact";
import BasicModal from "@/modules/common/components/modals/basic-modal";
import ModalHeader from "@/modules/common/components/modals/modal-header";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";

import "react-phone-input-2/lib/style.css";

import FullScreenDialog from "@/modules/common/components/modals/full-screen-modal";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import ContactPhone from "@/modules/lead-page/components/lead-info/contact-phone";
import ContactWhatsapp from "@/modules/lead-page/components/lead-info/contact-whatsapp";
import SearchTruecaller from "@/modules/lead-page/components/lead-info/search-truecaller";
import { muiTheme } from "@/styles/theme";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { Trash2 } from "lucide-react";
import { UUID } from "crypto";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import { MuiTelInput, type MuiTelInputInfo } from "mui-tel-input";

const EditTelephone = ({
  open,
  setOpen,
  telephoneHook,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  telephoneHook: {
    state: string;
    setTelephone: React.Dispatch<React.SetStateAction<string>>;
    telephoneList:
      | {
          uid: UUID;
          telephone: string;
          unifiedTelephone?: string | null;
          contactType?: "phone" | "whatsapp";
          whatsappUsername?: string | null;
          primary: boolean;
          id: number;
          deletedAt: string | null;
        }[]
      | undefined;
    deleteTelephone: {
      setTelephoneIdToDelete: React.Dispatch<React.SetStateAction<UUID | null>>;
      telephoneIdToDelete: UUID | null;
      isDeletingTelephone: boolean;
      handleDeleteTelephone: (telephoneId: UUID) => void;
    };
    addTelephone: {
      isAddingTelephone: boolean;
      isErrorAddTelephone: boolean;
      addTelephoneValidationError: string;
      handleAddTelephone: () => void;
    };
    makeTelephonePrimary: {
      handleMakeTelephonePrimary: (telephoneId: UUID) => void;
      isMakingTelephonePrimary: boolean;
      isErrorMakeTelephonePrimary: boolean;
      setTelephoneIdToMakeDefault: React.Dispatch<
        React.SetStateAction<UUID | null>
      >;
      telephoneIdToMakeDefault: UUID | null;
    };
    handleChangeTelephone: (e: React.ChangeEvent<HTMLInputElement>) => void;
  };
}) => {
  const screen = useScreenSize();
  const handlePhoneChange = (value: string, info: MuiTelInputInfo) => {
    const defaultDialCode = "+20";
    const dialCode = info.countryCallingCode
      ? `+${info.countryCallingCode}`
      : defaultDialCode;

    let updatedValue = value;
    if (!value.startsWith(dialCode)) {
      updatedValue = `${dialCode} ${value.replace(/^\+?\d*/, "").trim()}`;
    }

    const customEvent = {
      target: {
        value: updatedValue,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    telephoneHook.handleChangeTelephone(customEvent);
  };
  const renderContent = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: "32px",
        }}
      >
        <ModalHeader
          handleClose={() => {
            setOpen(false);
            telephoneHook.setTelephone("");
          }}
          headerText="Contact details"
        />
        {telephoneHook.telephoneList?.map((telephone) => {
          const contact = resolveCustomerContact([{ ...telephone, deletedAt: null }]);
          if (!telephone.telephone)
            return (
              <CustomerContactDisplay
                stacked
                key={telephone.uid}
                contacts={[telephone]}
              />
            );
          return (
            <Box
              key={telephone.id}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
                gap: 2,
                padding: "8px 10px 8px 8px",
                border: telephone?.primary ? "1px solid #A9C7DB" : "",
                backgroundColor: telephone?.primary ? "background.blue1" : "",
                borderRadius: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Typography variant="body_sm_reg" color="#3A404A">
                    <CustomerContactDisplay contacts={[{ ...telephone, deletedAt: null }]} actions="copy" />
                  </Typography>

                  <Stack direction="row" spacing={1}>
                    {contact.phone && !telephone.deletedAt ? <>
                      <ContactPhone phoneNumber={contact.phone} />
                      <ContactWhatsapp phoneNumber={contact.phone} />
                    </> : <Typography variant="body_sm_reg">Phone number unavailable</Typography>}

                    <Box
                      sx={{
                        display: "flex",
                        width: "100%",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      {telephone?.primary ? (
                        <Typography
                          sx={{
                            color:
                              muiTheme.colorSchemes.light.palette.neutral[800],
                          }}
                          variant="body_sm_reg"
                        >
                          Main
                        </Typography>
                      ) : telephoneHook.makeTelephonePrimary
                          .isMakingTelephonePrimary &&
                        telephoneHook.makeTelephonePrimary
                          .telephoneIdToMakeDefault == telephone.uid ? (
                        <>
                          <Box
                            sx={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <CircularProgress size={20} color="primary" />
                          </Box>
                        </>
                      ) : (
                        telephone.deletedAt === null && (
                          <Typography
                            sx={{
                              color: "#015C9D",
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                            variant="body_sm_reg"
                            onClick={() => {
                              telephoneHook.makeTelephonePrimary.setTelephoneIdToMakeDefault(
                                telephone.uid,
                              );
                              telephoneHook.makeTelephonePrimary.handleMakeTelephonePrimary(
                                telephone.uid,
                              );
                            }}
                          >
                            Set as Main
                          </Typography>
                        )
                      )}
                    </Box>
                  </Stack>
                </Box>

                <PermissionGuard
                  permissions={[
                    {
                      name: PermissionsEnum.DELETE_CUSTOMER_TELEPHONE,
                      level: PermissionLevel.WRITE,
                    },
                  ]}
                >
                  <>
                    {telephoneHook.deleteTelephone.isDeletingTelephone &&
                    telephoneHook.deleteTelephone.telephoneIdToDelete ==
                      telephone.uid ? (
                      <CircularProgress size={20} color="primary" />
                    ) : !telephone.primary && telephone.deletedAt === null ? (
                      <Trash2
                        width={15}
                        height={16}
                        style={{
                          color: muiTheme.colorSchemes.light.palette.error[300],
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          telephoneHook.deleteTelephone.setTelephoneIdToDelete(
                            telephone.uid,
                          );
                          telephoneHook.deleteTelephone.handleDeleteTelephone(
                            telephone.uid,
                          );
                        }}
                      />
                    ) : (
                      telephone.primary &&
                      telephoneHook.telephoneList?.length === 1 &&
                      telephone.deletedAt !== null && (
                        <Trash2
                          width={15}
                          height={16}
                          style={{
                            color:
                              muiTheme.colorSchemes.light.palette.error[300],
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            telephoneHook.deleteTelephone.setTelephoneIdToDelete(
                              telephone.uid,
                            );
                            telephoneHook.deleteTelephone.handleDeleteTelephone(
                              telephone.uid,
                            );
                          }}
                        />
                      )
                    )}

                    {telephone.deletedAt !== null && (
                      <Typography variant="body_sm_reg" color="textSecondary">
                        {"Deleted"}
                      </Typography>
                    )}
                  </>
                </PermissionGuard>
              </Box>

              <SearchTruecaller phoneNumber={telephone.deletedAt ? "" : contact.phone ?? ""} />
            </Box>
          );
        })}
        <Box
          sx={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: "80%",
            }}
          >
            {" "}
            <MuiTelInput
              label="Phone number"
              error={!!telephoneHook.addTelephone.addTelephoneValidationError}
              helperText={telephoneHook.addTelephone.addTelephoneValidationError}
              value={telephoneHook.state}
              defaultCountry="EG"
              onChange={(value, info) => {
                handlePhoneChange(value, info);
              }}
              fullWidth
              size="small"
              placeholder="New Phone Number"
            />
          </Box>
          <Button
            variant="contained"
            color="primary"
            sx={{
              textTransform: "none",
              padding: "8px 18px",
              borderRadius: "8px",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
            disabled={
              telephoneHook.addTelephone.isAddingTelephone ||
              telephoneHook.state === "" ||
              telephoneHook.addTelephone.addTelephoneValidationError.length > 0
            }
            onClick={() => telephoneHook.addTelephone.handleAddTelephone()}
          >
            {telephoneHook.addTelephone.isAddingTelephone ? (
              <CircularProgress size={20} color="primary" />
            ) : (
              <>
                <AddOutlinedIcon />
                Add
              </>
            )}
          </Button>
        </Box>
      </Box>
    );
  };
  return (
    <>
      {screen.isMobile ? (
        <FullScreenDialog
          open={open}
          setOpen={setOpen}
          header="Contact details"
          handleCloseModal={() => {
            setOpen(false);
            telephoneHook.setTelephone("");
          }}
        >
          {renderContent()}
        </FullScreenDialog>
      ) : (
        <BasicModal open={open} setOpen={setOpen} width={450}>
          {renderContent()}
        </BasicModal>
      )}
    </>
  );
};

export default EditTelephone;
